// Gestão de passkeys (biometria) do usuário logado.
// Cadastrar nova passkey, listar dispositivos, renomear, excluir.

import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  Fingerprint,
  Globe,
  Loader2,
  Pencil,
  Plus,
  Smartphone,
  Trash2,
  X
} from "lucide-react";
import {
  startRegistration,
  browserSupportsWebAuthn
} from "@simplewebauthn/browser";
import { api, type PasskeyDevice } from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

export function detectDeviceMeta(): Record<string, string | undefined> {
  const ua = navigator.userAgent;
  // UA Client Hints API (Chromium-only). Não tem types nativos.
  const uad = (navigator as any).userAgentData as
    | { brands?: { brand: string; version: string }[]; platform?: string; mobile?: boolean }
    | undefined;
  let browser = "";
  if (uad?.brands?.length) {
    const main = uad.brands.find((b) => !/Not.A.Brand/i.test(b.brand));
    if (main) browser = main.brand;
  } else {
    if (/Edg\//.test(ua)) browser = "Edge";
    else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = "Chrome";
    else if (/Firefox\//.test(ua)) browser = "Firefox";
    else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = "Safari";
  }
  let os = "";
  if (/Windows/.test(ua)) os = "Windows";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iOS/.test(ua)) os = "iOS";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";
  let model = "";
  const m = ua.match(/\(([^)]+)\)/);
  if (m) {
    const inner = m[1] || "";
    const semi = inner.split(";").map((s) => s.trim());
    const last = semi[semi.length - 1];
    if (last && !/Win|Mac|Linux|Android|iPhone/i.test(last)) model = last;
  }
  return {
    browser,
    os,
    model,
    platform: uad?.platform || "",
    ua: ua.slice(0, 400)
  };
}

function currentDeviceLabel(meta: Record<string, string | undefined>): string {
  const browser = meta.browser?.trim();
  const os = meta.os?.trim();
  const model = meta.model?.trim();
  if (model && os) return `${model} · ${os}`;
  if (browser && os) return `${browser} · ${os}`;
  return model || browser || os || "Este dispositivo";
}

function normalize(v: string | null | undefined): string {
  return (v || "").trim().toLowerCase();
}

function matchesCurrentDevice(
  passkey: Pick<PasskeyDevice, "browser" | "os" | "model" | "platform">,
  meta: Record<string, string | undefined>
): boolean {
  const browser = normalize(meta.browser);
  const os = normalize(meta.os || meta.platform);
  const model = normalize(meta.model);
  const passkeyBrowser = normalize(passkey.browser);
  const passkeyOs = normalize(passkey.os || passkey.platform);
  const passkeyModel = normalize(passkey.model);

  if (browser && passkeyBrowser && browser !== passkeyBrowser) return false;
  if (os && passkeyOs && os !== passkeyOs) return false;
  if (model && passkeyModel && model !== passkeyModel) return false;
  return !!(browser || os || model);
}

export function supportsBiometria(): boolean {
  return browserSupportsWebAuthn();
}

export async function registerCurrentDevicePasskey(): Promise<void> {
  const opts = await api.passkeyRegisterOptions();
  const credential = await startRegistration({ optionsJSON: opts.options });
  const meta = detectDeviceMeta();
  await api.passkeyRegisterVerify({
    challengeId: opts.challengeId,
    credential,
    deviceLabel: currentDeviceLabel(meta),
    deviceMeta: meta
  });
}

export async function removeCurrentDevicePasskeys(): Promise<number> {
  const meta = detectDeviceMeta();
  const res = await api.listPasskeys();
  let matches = res.passkeys.filter((p) => matchesCurrentDevice(p, meta));
  if (matches.length === 0 && res.passkeys.length === 1) matches = res.passkeys;
  await Promise.all(matches.map((p) => api.deletePasskey(p.id)));
  return matches.length;
}

/**
 * Conta passkeys registradas pra ESTE dispositivo. Usado pelas Preferências
 * pra hidratar o toggle de biometria — se existe passkey, toggle inicia ON.
 */
export async function countCurrentDevicePasskeys(): Promise<number> {
  try {
    const meta = detectDeviceMeta();
    const res = await api.listPasskeys();
    const matches = res.passkeys.filter((p) => matchesCurrentDevice(p, meta));
    // Fallback: se só tem 1 passkey total e nenhum match exato, considera-a
    // como deste dispositivo (mesmo critério do remove). Cobre o caso comum
    // de UA-parsing imperfeito mas usuário tem só 1 device cadastrado.
    if (matches.length === 0 && res.passkeys.length === 1) {
      return 1;
    }
    return matches.length;
  } catch {
    return 0;
  }
}

export function BiometriaSection() {
  const [items, setItems] = useState<PasskeyDevice[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameInput, setRenameInput] = useState("");
  const supports = supportsBiometria();

  async function reload() {
    try {
      const r = await api.listPasskeys();
      setItems(r.passkeys);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  // Após a coleta da credencial via WebAuthn, guardamos os dados aqui e
  // abrimos o LabelModal — o user escolhe o nome no nosso padrão (não no
  // prompt() nativo que aparecia bagunçando o visual).
  const [pendingLabel, setPendingLabel] = useState<{
    challengeId: string;
    credential: any;
  } | null>(null);

  async function register() {
    setErr(null);
    setOkMsg(null);
    setBusy(true);
    try {
      const opts = await api.passkeyRegisterOptions();
      const credential = await startRegistration({ optionsJSON: opts.options });
      setPendingLabel({ challengeId: opts.challengeId, credential });
    } catch (e: any) {
      console.warn("[biometria] register falhou:", e);
      const msg = e?.message || String(e);
      if (/cancelled|aborted|NotAllowed/i.test(msg)) {
        setErr("Cadastro cancelado. Toque em Cadastrar de novo e confirme com sua digital / Face ID quando o sistema pedir.");
      } else {
        setErr(
          friendlyError(e, {
            fallback:
              "Não foi possível cadastrar a biometria agora. Confira se seu celular tem PIN/biometria configurados e tente de novo."
          })
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function finishRegister(label: string) {
    if (!pendingLabel) return;
    setBusy(true);
    try {
      await api.passkeyRegisterVerify({
        challengeId: pendingLabel.challengeId,
        credential: pendingLabel.credential,
        deviceLabel: label.trim() || undefined,
        deviceMeta: detectDeviceMeta()
      });
      setPendingLabel(null);
      setOkMsg("Biometria cadastrada!");
      setTimeout(() => setOkMsg(null), 2500);
      void reload();
    } catch (e: any) {
      console.warn("[biometria] verify falhou:", e);
      setErr(
        friendlyError(e, {
          fallback: "Não foi possível concluir o cadastro. Tente de novo."
        })
      );
    } finally {
      setBusy(false);
    }
  }

  async function startRename(p: PasskeyDevice) {
    setRenamingId(p.id);
    setRenameInput(p.name || "");
  }

  async function commitRename(p: PasskeyDevice) {
    try {
      await api.renamePasskey(p.id, renameInput);
      setRenamingId(null);
      void reload();
    } catch (e) {
      void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function remove(p: PasskeyDevice) {
    if (!confirm(`Remover "${p.name || p.autoLabel || "passkey"}" deste dispositivo?`)) {
      return;
    }
    try {
      await api.deletePasskey(p.id);
      void reload();
    } catch (e) {
      void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <section class="rounded-md border border-border bg-card/60 p-5">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 class="flex items-center gap-2 text-sm font-bold">
          <Fingerprint className="size-4 text-primary" />
          Biometria e Passkeys
        </h2>
        <button
          type="button"
          onClick={register}
          disabled={!supports || busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
          Cadastrar este dispositivo
        </button>
      </div>

      <p class="mb-3 text-xs text-muted-foreground">
        Use sua impressão digital, Face ID ou PIN do dispositivo pra entrar sem
        senha. Cada dispositivo cadastra a própria credencial — você pode ter
        vários e revogar individualmente.
      </p>

      {!supports && (
        <div class="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-700 dark:text-amber-400">
          Seu navegador não suporta WebAuthn. Use Chrome, Edge, Safari ou
          Firefox recentes em HTTPS (ou localhost).
        </div>
      )}

      {okMsg && (
        <div class="mb-3 flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[11px] text-emerald-700 dark:text-emerald-400">
          <Check className="size-3.5" />
          {okMsg}
        </div>
      )}
      {err && (
        <div class="mb-3 flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11px] text-destructive">
          <AlertCircle className="size-3.5" />
          {err}
        </div>
      )}

      {loading ? (
        <p class="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Carregando dispositivos…
        </p>
      ) : items && items.length === 0 ? (
        <p class="text-xs italic text-muted-foreground">
          Nenhum dispositivo cadastrado ainda.
        </p>
      ) : (
        <ul class="space-y-2">
          {items?.map((p) => (
            <li
              key={p.id}
              class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-background/60 px-3 py-2"
            >
              <div class="grid size-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
                {p.model || p.platform === "Android" || p.platform === "iOS" ? (
                  <Smartphone className="size-4" />
                ) : (
                  <Globe className="size-4" />
                )}
              </div>
              <div class="min-w-0 flex-1">
                {renamingId === p.id ? (
                  <div class="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={renameInput}
                      onInput={(e) => setRenameInput((e.target as HTMLInputElement).value)}
                      placeholder="Nome amigável"
                      maxLength={80}
                      class="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => commitRename(p)}
                      class="grid size-7 place-items-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                      title="Salvar"
                    >
                      <Check className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenamingId(null)}
                      class="grid size-7 place-items-center rounded-md border border-border text-muted-foreground hover:bg-accent"
                      title="Cancelar"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div class="truncate text-sm font-semibold">
                      {p.name || p.autoLabel || "Dispositivo sem nome"}
                    </div>
                    <div class="truncate text-[10px] text-muted-foreground">
                      {[p.browser, p.os, p.platformVersion]
                        .filter(Boolean)
                        .join(" · ")}
                      {" · cadastrado em "}
                      {new Date(p.createdAt).toLocaleDateString("pt-BR")}
                      {p.lastUsedAt &&
                        ` · último uso ${new Date(p.lastUsedAt).toLocaleDateString("pt-BR")}`}
                    </div>
                  </>
                )}
              </div>
              {renamingId !== p.id && (
                <div class="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => startRename(p)}
                    title="Renomear"
                    aria-label="Renomear"
                    class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(p)}
                    title="Excluir"
                    aria-label="Excluir"
                    class="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {pendingLabel && (
        <LabelDeviceModal
          busy={busy}
          onClose={() => setPendingLabel(null)}
          onConfirm={(name) => void finishRegister(name)}
        />
      )}
    </section>
  );
}

function LabelDeviceModal({
  busy,
  onClose,
  onConfirm
}: {
  busy: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState("");
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        class="w-full max-w-md rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Fingerprint className="size-4 text-primary" />
            Como chamar este dispositivo?
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title="Cancelar"
            aria-label="Cancelar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="space-y-3 px-5 py-4 text-sm">
          <p class="text-xs text-muted-foreground">
            Um nome ajuda você a identificar este dispositivo na lista (ex.:
            "iPhone Luis", "PC trabalho"). Pode deixar em branco — usamos um
            nome automático com base no navegador/SO.
          </p>
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            placeholder="Nome amigável (opcional)"
            maxLength={80}
            title="Nome do dispositivo"
            aria-label="Nome do dispositivo"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && !busy) onConfirm(name);
            }}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>
        <footer class="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(name)}
            disabled={busy}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Check className="size-3.5" />
            )}
            Cadastrar
          </button>
        </footer>
      </div>
    </div>
  );
}
